import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bell,
  CheckCircle2,
  UserPlus,
  UserCheck,
  TrendingUp,
  Pencil,
  CalendarCheck,
  Paperclip,
  Star,
  type LucideIcon,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import { SkyBadge, SkyButton, SkyLoader, SkyPopover } from "@skypointcloud/skypoint-ui";
import { httpClient } from "../../lib/http-client";
import { useMsalAuth } from "../../hooks/useMsalAuth";
import { usePacks } from "../packs/PackContext";
import { useNotificationEvents, type AppNotification } from "./NotificationEventContext";
import { logger } from "../../services/logger.service";
import { formatInstant } from "../../common/utilities/displayTime";
import { relativeAge } from "../../utils/relativeAge";
import { useDisplayTz } from "../../hooks/useDisplayTz";

/**
 * Persisted, per-user notification bell + inbox panel.
 *
 * The WelcomeHome-parity surface the ephemeral toast bus never provided: a badge
 * + inbox backed by public.crm_notifications (keyed user_email + instance_id),
 * surviving refresh and targeted to the signed-in user.
 *
 * UI: built strictly on the SkyPoint design system — Sky* primitives carry the
 * Fluent-2 tokens (color/type/spacing), so there are no raw hex / gray-N / blue-N
 * literals here. Overlay is a Popover (design rule: a surface tied to a trigger
 * uses a popover, not a drawer/modal). Reuses the EXISTING WS connection via
 * NotificationEventContext (no second socket) and only reacts to PERSISTED types
 * so the badge never over-counts the legacy broadcast toasts.
 */

// Notification types persisted in public.crm_notifications. Grows as later
// increments migrate more events onto the per-user spine.
const PERSISTED_TYPES = new Set<AppNotification["type"]>([
  "prospect_reassigned",
  // Feature B (D12): role-reassignment summary to the incoming holder.
  "items_reassigned",
  "prospect_created",
  "prospect_updated",
  "prospect_advanced",
  "event_rsvped",
  "attachment_uploaded",
  // SW-CRM-PROSPECT Follow → notifications: follower-stream rows persist too.
  "prospect_followed",
  // PLC W12 Inc-3b: a coverage window names you as the coverer.
  "coverage_assigned",
]);

interface InboxItem {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string | null;
  read_at: string | null;
  created_at: string;
}

/** One page of GET /crm/notifications. `nextCursor` is the OPAQUE keyset token
 *  the server built from the last row of this page — pass it straight back as
 *  `before` to fetch the next (older) page. Never parse it here.
 *
 *  The server also sends `hasMore`, but it is definitionally `nextCursor !== null`
 *  (it derives one from the other), so we hold ONLY the cursor. Mirroring both
 *  into state would allow a skew — `hasMore` true with a null cursor — that
 *  renders a pager the fetch then refuses to act on. */
interface InboxPage {
  items: InboxItem[];
  nextCursor: string | null;
}

// Rows per request. The server REJECTS a `limit` above 50 with a 400 — it does
// not clamp (`z.coerce.number().int().min(1).max(50)` in crmNotifications.ts),
// so raising this past 50 breaks the inbox rather than silently capping it. 20
// keeps the first paint cheap and matches the page size the panel was designed
// around.
const PAGE_SIZE = 20;

// Per-type icon + tint — mirrors the canonical app pattern (ActivityTimeline's TYPES):
// a lucide icon in a tinted circle, one color per event type so each row reads at a
// glance. Same palette language (green/blue/violet/amber/rose/slate).
interface TypeVisual {
  icon: LucideIcon;
  iconClass: string;
  bgClass: string;
}
const TYPE_VISUAL: Record<string, TypeVisual> = {
  prospect_created: { icon: UserPlus, iconClass: "text-green-600", bgClass: "bg-green-50" },
  prospect_reassigned: { icon: UserCheck, iconClass: "text-blue-600", bgClass: "bg-blue-50" },
  prospect_advanced: { icon: TrendingUp, iconClass: "text-violet-600", bgClass: "bg-violet-50" },
  prospect_updated: { icon: Pencil, iconClass: "text-amber-600", bgClass: "bg-amber-50" },
  event_rsvped: { icon: CalendarCheck, iconClass: "text-rose-600", bgClass: "bg-rose-50" },
  // Tour host heads-up (persisted, in addition to the transient toast): a calendar glyph in a
  // distinct teal tint so a booked/rescheduled/cancelled-tour row reads apart from event RSVPs.
  tour_booked: { icon: CalendarCheck, iconClass: "text-teal-600", bgClass: "bg-teal-50" },
  attachment_uploaded: { icon: Paperclip, iconClass: "text-slate-600", bgClass: "bg-slate-100" },
  // Follower stream: a star (the universal "following" marker) in a distinct indigo
  // tint so a followed-prospect row reads apart from the staff-broadcast rows above.
  prospect_followed: { icon: Star, iconClass: "text-indigo-600", bgClass: "bg-indigo-50" },
};
const FALLBACK_VISUAL: TypeVisual = {
  icon: Bell,
  iconClass: "text-gray-500",
  bgClass: "bg-gray-100",
};

// `zone` = the viewer's resolved display zone (no-record). The relative parts (just now / Xm /
// Xh / Xd ago) are DURATIONS (tz-agnostic, kept); only the >7d fallback is an INSTANT-as-date,
// rendered in the resolved zone (was browser-local toLocaleDateString).
const timeAgo = (iso: string, zone: string): string =>
  relativeAge(iso, {
    beyond: { afterDays: 7, render: (v) => formatInstant(v, zone, { showTime: false }) },
  });

/**
 * Pack gate (QA incident 2026-07-08, instance 323f0dc4): the bell is a
 * CRM surface (public.crm_notifications), so it must only exist on
 * instances with an operator pack — the client twin of the server's
 * requirePack gate on /api/v1/crm. On advisor-only instances the inner
 * component must not MOUNT at all (mounting = polling /crm/* = a
 * correct-but-fatal 403 through the global errorHandler → full-screen
 * error → remount → refetch loop). Gating happens in this wrapper so
 * none of the inner hooks/effects ever run.
 */
export function NotificationBell() {
  const { hasOperatorPack } = usePacks();
  if (!hasOperatorPack) return null;
  return <NotificationBellInner />;
}

function NotificationBellInner() {
  const { getInstanceId, isLoggedIn } = useMsalAuth();
  const { subscribe } = useNotificationEvents();
  const navigate = useNavigate();
  // Notification timestamp >7d fallback renders in the viewer's resolved zone (no-record).
  const zone = useDisplayTz();

  const [instanceId, setInstanceId] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  // The first page failed. Distinct from "empty": rendering "You are all caught
  // up" after a network failure states something we do not know to be true.
  const [loadError, setLoadError] = useState(false);
  // Keyset pagination state. `nextCursor` is the opaque token for the page AFTER
  // the oldest row currently held; null once the inbox is exhausted (and so the
  // single source of truth for whether a pager renders).
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // A "load older" click FAILED. Distinct from `loadError` (which owns the first
  // page and replaces the whole list): here the rows on screen are still valid,
  // so the list stays and only the pager reports. Without this the button simply
  // flips back to its idle label and the click reads as a no-op.
  const [pageError, setPageError] = useState(false);
  // Generation guard: bumped on every FRESH (cursor-less) load. A "load more"
  // discards its page if a fresh load has happened since — otherwise reopening
  // the panel, or switching instance mid-flight, could append a stale page onto
  // a freshly-replaced list.
  const loadGen = useRef(0);
  // The generation the CURRENT `nextCursor` belongs to. `loadMore` pins to this
  // rather than to loadGen.current, which a fresh load already in flight would
  // have bumped past — reading the live value there would let a page derived
  // from the OLD list pass the guard and append onto the new one.
  const cursorGen = useRef(0);
  // In-flight latch for `loadMore`. A ref, not the `loadingMore` state: the state
  // a handler closes over is the value at RENDER time, so two clicks landing
  // before the re-render would both pass a state-based guard. This is written
  // synchronously, so the second click always sees it.
  const loadingMoreRef = useRef(false);
  // Supersede guard for the badge count. Bumped by every unread fetch AND by
  // every local mark-read, so an in-flight count that resolves after the user
  // clears the inbox cannot resurrect the badge.
  const unreadGen = useRef(0);

  // Resolve instanceId from the current route — re-evaluates on navigation (incl.
  // instance switches, which change the URL) instead of a 2s polling timer.
  // setInstanceId(sameValue) no-ops, so navigating WITHIN an instance never
  // re-fetches; only an actual instance change does.
  const location = useLocation();
  useEffect(() => {
    setInstanceId(isLoggedIn ? getInstanceId() || "" : "");
  }, [location.pathname, location.search, isLoggedIn, getInstanceId]);

  // skipGlobalErrorHandler on every bell request (safety net behind the
  // pack gate above): a badge/inbox failure must NEVER route through the
  // global errorHandler and blank the whole app — the bell degrades to
  // "no badge" and logs, nothing more.
  const fetchUnread = useCallback(async (iid: string) => {
    const gen = ++unreadGen.current;
    try {
      const res = await httpClient.get<{ count: number }>(
        `/api/v1/crm/notifications/unread-count?instanceId=${iid}`,
        { skipGlobalErrorHandler: true },
      );
      // A mark-read/mark-all-read landed while this was in flight — that local
      // truth is newer than this server count, so drop it rather than flash the
      // badge back on.
      if (gen !== unreadGen.current) return;
      setUnread(res.data?.count ?? 0);
    } catch (err) {
      logger.error("[NotificationBell] unread-count failed", err);
    }
  }, []);

  // FIRST page: replaces the list and resets the cursor. Every open starts here,
  // so a stale cursor from a previous instance/session can never be reused.
  //
  // The reset happens UP FRONT, not on success. Clearing only in the `try` meant
  // a failed load left the PREVIOUS instance's rows and cursor on screen with a
  // live pager — and "Load older" then sent the old instance's cursor with the
  // new instanceId, appending the new instance's rows underneath the old one's.
  // `loading` covers the blanked list, so there is no flash on the happy path.
  const fetchInbox = useCallback(async (iid: string) => {
    const gen = ++loadGen.current;
    setLoading(true);
    setLoadError(false);
    setPageError(false);
    setItems([]);
    setNextCursor(null);
    cursorGen.current = gen;
    try {
      const res = await httpClient.get<InboxPage>(
        `/api/v1/crm/notifications?instanceId=${iid}&limit=${PAGE_SIZE}`,
        { skipGlobalErrorHandler: true },
      );
      if (gen !== loadGen.current) return;
      // MERGE, don't replace. The list was blanked up front, so anything in `prev`
      // is a row the WS prepended WHILE this request was in flight — newer than
      // every row in the page. A flat `setItems(page)` dropped it: the badge had
      // already counted it, so the panel showed one fewer row than it claimed.
      // Live rows go back on top; the page's own copy wins on id (it carries the
      // authoritative read_at).
      const page = res.data?.items ?? [];
      setItems((live) => {
        if (live.length === 0) return page;
        const inPage = new Set(page.map((i) => i.id));
        return [...live.filter((i) => !inPage.has(i.id)), ...page];
      });
      setNextCursor(res.data?.nextCursor ?? null);
      cursorGen.current = gen;
    } catch (err) {
      logger.error("[NotificationBell] inbox load failed", err);
      if (gen === loadGen.current) setLoadError(true);
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, []);

  // NEXT page: APPENDS older rows behind the keyset cursor. Dedupes by id because
  // the same notification can also arrive over the WS live-prepend below — without
  // this, a socket row already at the top would repeat in a later page (duplicate
  // React keys, and the row rendered twice).
  const loadMore = useCallback(async () => {
    if (!instanceId || !nextCursor || loadingMoreRef.current) return;
    // Pin to the generation the CURSOR was issued in, and refuse to start at all
    // if a fresh load has already superseded it.
    const gen = cursorGen.current;
    if (gen !== loadGen.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setPageError(false);
    try {
      const res = await httpClient.get<InboxPage>(
        `/api/v1/crm/notifications?instanceId=${instanceId}&limit=${PAGE_SIZE}` +
          `&before=${encodeURIComponent(nextCursor)}`,
        { skipGlobalErrorHandler: true },
      );
      // A fresh load landed while this page was in flight — drop it.
      if (gen !== loadGen.current) return;
      const page = res.data?.items ?? [];
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...page.filter((i) => !seen.has(i.id))];
      });
      // The cursor advances but stays in the same generation. Left intact on
      // failure, so the pager survives and the click is simply retryable.
      setNextCursor(res.data?.nextCursor ?? null);
      cursorGen.current = gen;
    } catch (err) {
      logger.error("[NotificationBell] inbox page load failed", err);
      // Only report if this request still owns the pager — a superseded page
      // must not paint an error over a freshly-loaded list.
      if (gen === loadGen.current) setPageError(true);
    } finally {
      // Always clear — unlike `loading` (which a newer fresh load owns), this flag
      // belongs to THIS request, and leaving it set would wedge the button.
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [instanceId, nextCursor]);

  // Initial + on-instance-change load of the badge count.
  useEffect(() => {
    if (instanceId) fetchUnread(instanceId);
  }, [instanceId, fetchUnread]);

  // Load the list when the panel opens, and re-reconcile the badge with the
  // server at the same time — between mount and open the count only ever moved
  // by local WS bumps and local mark-reads, so a mark-all-read in another tab
  // (or an email-channel read) would otherwise leave the badge stale forever.
  useEffect(() => {
    if (open && instanceId) {
      fetchInbox(instanceId);
      fetchUnread(instanceId);
    }
  }, [open, instanceId, fetchInbox, fetchUnread]);

  // Live bumps over the EXISTING WS connection (persisted types only).
  useEffect(() => {
    const unsub = subscribe((n: AppNotification) => {
      if (!PERSISTED_TYPES.has(n.type)) return;
      // Supersede any in-flight unread-count fetch, exactly as markRead does. The
      // service persists the row BEFORE it pushes, but a count whose SELECT ran
      // before that commit resolves afterwards with the pre-row number — and
      // without this bump it passes the guard and overwrites the increment, so a
      // live notification arrives with the badge silently unchanged.
      unreadGen.current++;
      setUnread((c) => c + 1);
      setItems((prev) => [
        {
          id: n.id,
          type: n.type,
          title: n.title,
          body: n.body,
          url: n.link ?? null,
          read_at: null,
          created_at: n.timestamp || new Date().toISOString(),
        },
        ...prev,
      ]);
    });
    return unsub;
  }, [subscribe]);

  const markRead = useCallback(
    async (item: InboxItem) => {
      if (!item.read_at) {
        // Supersede any in-flight unread-count fetch: this local truth is newer.
        unreadGen.current++;
        setItems((prev) =>
          prev.map((i) => (i.id === item.id ? { ...i, read_at: new Date().toISOString() } : i)),
        );
        setUnread((c) => Math.max(0, c - 1));
        try {
          await httpClient.post(
            `/api/v1/crm/notifications/${item.id}/read?instanceId=${instanceId}`,
            undefined,
            { skipGlobalErrorHandler: true },
          );
        } catch (err) {
          logger.error("[NotificationBell] mark-read failed", err);
        }
      }
      if (item.url) {
        setOpen(false);
        navigate(item.url);
      }
    },
    [instanceId, navigate],
  );

  const markAllRead = useCallback(async () => {
    // Supersede any in-flight unread-count fetch (see markRead).
    unreadGen.current++;
    setItems((prev) => prev.map((i) => ({ ...i, read_at: i.read_at ?? new Date().toISOString() })));
    setUnread(0);
    try {
      await httpClient.post(
        `/api/v1/crm/notifications/read-all?instanceId=${instanceId}`,
        undefined,
        {
          skipGlobalErrorHandler: true,
        },
      );
    } catch (err) {
      logger.error("[NotificationBell] read-all failed", err);
    }
  }, [instanceId]);

  if (!instanceId) return null;

  // Trigger: transparent icon button (matches the header's other icon buttons),
  // with the unread count as a brand badge overlay.
  const trigger = (
    <span className="relative inline-flex">
      <SkyButton
        icon={Bell}
        appearance="transparent"
        className="text-white! min-w-10! h-10!"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        onClick={() => setOpen((o) => !o)}
      />
      {unread > 0 && (
        <span className="absolute top-0 right-0 pointer-events-none">
          <SkyBadge
            color="brand"
            size="small"
            maxCount={99}
            aria-label={`${unread} unread`}
          >
            {unread}
          </SkyBadge>
        </span>
      )}
    </span>
  );

  return (
    <SkyPopover
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      placement="bottom-end"
    >
      <div className="w-[380px] max-w-[90vw]">
        {/* Header: title + unread count + mark-all, with a real bottom border. */}
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 pb-2.5 pt-0.5">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-gray-900">Notifications</span>
            {unread > 0 && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-sm font-semibold text-amber-700">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </div>
          {unread > 0 && (
            <SkyButton
              appearance="transparent"
              size="small"
              onClick={markAllRead}
            >
              Mark all read
            </SkyButton>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto px-1.5 py-1.5 flex flex-col gap-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <SkyLoader label="Loading notifications" />
            </div>
          ) : loadError ? (
            // The load FAILED — say so. Falling through to "You are all caught up"
            // would assert an empty inbox we have no evidence for.
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <span className="text-sm text-gray-500">Could not load notifications</span>
              <SkyButton
                appearance="transparent"
                size="small"
                onClick={() => instanceId && fetchInbox(instanceId)}
              >
                Try again
              </SkyButton>
            </div>
          ) : items.length === 0 ? (
            // The one block the design system allows to center: empty state.
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <CheckCircle2
                className="h-8 w-8 text-gray-300"
                aria-hidden="true"
              />
              <span className="text-sm text-gray-500">You are all caught up</span>
            </div>
          ) : (
            items.map((item) => {
              const v = TYPE_VISUAL[item.type] ?? FALLBACK_VISUAL;
              const Icon = v.icon;
              const unread = !item.read_at;
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => markRead(item)}
                  className={`flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left ${
                    unread ? "bg-amber-50/70 hover:bg-amber-100/50" : ""
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${v.bgClass}`}
                  >
                    <Icon
                      size={16}
                      className={v.iconClass}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex items-start gap-2">
                      <span
                        className={`flex-1 truncate text-sm text-gray-900 ${
                          unread ? "font-semibold" : "font-medium"
                        }`}
                      >
                        {item.title}
                      </span>
                      {unread && (
                        <span
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500"
                          aria-label="Unread"
                        />
                      )}
                    </span>
                    <p className="line-clamp-2 text-sm text-gray-600">{item.body}</p>
                    <p className="text-sm text-gray-400">{timeAgo(item.created_at, zone)}</p>
                  </span>
                </button>
              );
            })
          )}

          {/* Older pages. Rendered inside the scroll container so it sits directly
              under the last row; gated on the CURSOR itself (not a mirrored
              `hasMore`), so the button can never render without a token to send,
              and the list ends cleanly once the inbox is exhausted. */}
          {!loading && !loadError && nextCursor !== null && (
            <div className="flex flex-col items-center gap-1 px-2 pb-1 pt-1">
              {/* A failed page keeps its cursor, so the button below is a real
                  retry — this only has to say the last click didn't land. */}
              {pageError && (
                <span
                  role="alert"
                  className="text-sm text-gray-500"
                >
                  Could not load older notifications
                </span>
              )}
              <SkyButton
                appearance="transparent"
                size="small"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading..." : pageError ? "Try again" : "Load older notifications"}
              </SkyButton>
            </div>
          )}
        </div>
      </div>
    </SkyPopover>
  );
}

export default NotificationBell;
